import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DiscountIcon,
  ExitIcon,
  HomeIcon,
  PersonIcon,
  StoreIcon,
  StoreManagedIcon,
} from '@shopify/polaris-icons'
import { Link, useLocation } from 'react-router-dom'

import { Logo } from '../../shared/components/Logo'
import { AdminPermission, useAdminAuth } from '../providers/AdminAuthProvider'
import { useFirebase } from '../providers/FirebaseProvider'
import { useSidebar } from '../providers/SidebarProvider'
import './AdminNavBar.css'

interface NavigationItem {
  id: string
  url: string
  label: string
  icon: React.ComponentType<any>
  permission?: AdminPermission
}

export interface AdminNavBarProps {
  isMobile: boolean
}

export function AdminNavBar({ isMobile }: AdminNavBarProps) {
  const {
    isMinimized,
    toggleSidebar,
    isMobileSidebarOpen,
    closeMobileSidebar,
  } = useSidebar()
  const location = useLocation()
  const { signOut } = useFirebase()
  const { hasPermission, adminUser } = useAdminAuth()

  const handleLogout = async () => {
    try {
      await signOut()
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  const handleToggleSidebar = () => {
    toggleSidebar()
  }

  const navigationItems: NavigationItem[] = [
    {
      id: 'dashboard',
      url: '/dashboard',
      label: 'Dashboard',
      icon: HomeIcon,
      permission: 'view_dashboard',
    },
    {
      id: 'shops',
      url: '/shops',
      label: 'Shops',
      icon: StoreIcon,
      permission: 'view_shops',
    },
    {
      id: 'test-stores',
      url: '/test-stores',
      label: 'Test stores',
      icon: StoreManagedIcon,
      permission: 'view_shops',
    },
    {
      id: 'discounts',
      url: '/discounts',
      label: 'Discounts',
      icon: DiscountIcon,
      permission: 'view_shops',
    },
    {
      id: 'accounts',
      url: '/accounts',
      label: 'Admin Accounts',
      icon: PersonIcon,
      permission: 'view_admin',
    },
  ]

  const filteredItems = navigationItems.filter(
    item => !item.permission || hasPermission(item.permission)
  )

  const isActive = (url: string) => location.pathname === url

  return (
    <div
      className={`nav-sidebar ${isMinimized ? 'minimized' : ''} ${isMobile && isMobileSidebarOpen ? 'mobile-open' : ''}`}
    >
      {/* Header */}
      <div
        className="sidebar-header"
        style={{
          flexDirection: isMinimized ? 'column' : 'row',
        }}
      >
        <Logo
          showTitle={!isMinimized}
          className="sidebar-logo"
          titleClassName="sidebar-title"
        />
        {isMobile && (
          <button
            className="toggle-button"
            onClick={handleToggleSidebar}
            aria-label={isMinimized ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isMinimized ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </button>
        )}
      </div>

      {/* Navigation Items */}
      <nav className="sidebar-nav">
        <ul className="nav-list">
          {filteredItems.map(item => {
            const IconComponent = item.icon
            const active = isActive(item.url)

            return (
              <li key={item.id} className="nav-item">
                <Link
                  to={item.url}
                  className={`nav-link ${active ? 'active' : ''}`}
                  title={isMinimized ? item.label : undefined}
                  onClick={() => {
                    if (isMobile && isMobileSidebarOpen) {
                      closeMobileSidebar()
                    }
                  }}
                >
                  <span className="nav-icon">
                    <IconComponent />
                  </span>
                  {!isMinimized && (
                    <span className="nav-label">{item.label}</span>
                  )}
                </Link>
              </li>
            )
          })}
        </ul>
      </nav>

      {/* User Section */}
      <div className="sidebar-footer">
        <div className="user-section">
          {!isMinimized && adminUser && (
            <Link
              to="/profile"
              className="profile-link"
              title="View Profile"
              style={{ textDecoration: 'none' }}
              onClick={() => {
                if (isMobile && isMobileSidebarOpen) {
                  closeMobileSidebar()
                }
              }}
            >
              <div className="user-info">
                <div className="user-avatar">
                  {adminUser.picture ? (
                    <img src={adminUser.picture} alt={adminUser.name} />
                  ) : (
                    <PersonIcon />
                  )}
                </div>
                <div className="user-details">
                  <span className="user-name">{adminUser.name}</span>
                  <span className="user-sub-name">{adminUser.id}</span>
                </div>
              </div>
            </Link>
          )}

          {/* Show user avatar in minimized mode */}
          {isMinimized && adminUser && (
            <Link
              to="/profile"
              className="profile-link-minimized"
              title={`${adminUser.name} - View Profile`}
              onClick={() => {
                if (isMobile && isMobileSidebarOpen) {
                  closeMobileSidebar()
                }
              }}
            >
              <div className="user-avatar">
                {adminUser.picture ? (
                  <img src={adminUser.picture} alt={adminUser.name} />
                ) : (
                  <PersonIcon />
                )}
              </div>
            </Link>
          )}

          <button
            className="logout-button"
            onClick={handleLogout}
            title="Logout"
          >
            <span className="nav-icon">
              <ExitIcon />
            </span>
            {!isMinimized && <span className="nav-label">Logout</span>}
          </button>
        </div>
      </div>
    </div>
  )
}
